import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, Tag, Calendar, Percent, DollarSign, Users, Check, X } from 'lucide-react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useToast } from '../../../hooks/useToast';
import { ToastContainer } from '../../../components/Toast';

interface PromoCode {
  id: string;
  code: string;
  discountType: 'percentage' | 'flat';
  discountValue: number;
  minOrderValue?: number;
  usageLimit?: number;
  usedCount: number;
  expiryDate?: string;
  isActive: boolean;
  description?: string;
  createdAt: any;
}

export default function PromoCodesView() {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState<PromoCode | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percentage' as 'percentage' | 'flat',
    discountValue: 0,
    minOrderValue: 0,
    usageLimit: 0,
    expiryDate: '',
    isActive: true,
    description: ''
  });
  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(collection(db, 'promoCodes'));
      const codes = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PromoCode[];
      
      // Sort by creation date
      codes.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });
      
      setPromoCodes(codes);
    } catch (err) {
      console.error('Error fetching promo codes:', err);
      error('Failed to fetch promo codes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.code.trim()) {
      error('Promo code is required');
      return;
    }
    
    if (formData.discountValue <= 0) {
      error('Discount value must be greater than 0');
      return;
    }
    
    if (formData.discountType === 'percentage' && formData.discountValue > 100) {
      error('Percentage discount cannot exceed 100%');
      return;
    }
    
    try {
      const promoData = {
        ...formData,
        code: formData.code.toUpperCase().trim(),
        usedCount: editingPromo ? editingPromo.usedCount : 0,
        createdAt: editingPromo ? editingPromo.createdAt : serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      if (editingPromo) {
        await updateDoc(doc(db, 'promoCodes', editingPromo.id), promoData);
        success('Promo code updated successfully');
      } else {
        await addDoc(collection(db, 'promoCodes'), promoData);
        success('Promo code created successfully');
      }
      
      setShowModal(false);
      setEditingPromo(null);
      resetForm();
      fetchPromoCodes();
    } catch (err) {
      console.error('Error saving promo code:', err);
      error('Failed to save promo code');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this promo code?')) return;
    
    try {
      await deleteDoc(doc(db, 'promoCodes', id));
      success('Promo code deleted');
      fetchPromoCodes();
    } catch (err) {
      console.error('Error deleting promo code:', err);
      error('Failed to delete promo code');
    }
  };

  const handleEdit = (promo: PromoCode) => {
    setEditingPromo(promo);
    setFormData({
      code: promo.code,
      discountType: promo.discountType,
      discountValue: promo.discountValue,
      minOrderValue: promo.minOrderValue || 0,
      usageLimit: promo.usageLimit || 0,
      expiryDate: promo.expiryDate || '',
      isActive: promo.isActive,
      description: promo.description || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discountType: 'percentage',
      discountValue: 0,
      minOrderValue: 0,
      usageLimit: 0,
      expiryDate: '',
      isActive: true,
      description: ''
    });
  };

  const handleToggleActive = async (promo: PromoCode) => {
    try {
      await updateDoc(doc(db, 'promoCodes', promo.id), {
        isActive: !promo.isActive,
        updatedAt: serverTimestamp()
      });
      success(`Promo code ${!promo.isActive ? 'activated' : 'deactivated'}`);
      fetchPromoCodes();
    } catch (err) {
      console.error('Error toggling promo code:', err);
      error('Failed to update promo code');
    }
  };

  const isExpired = (expiryDate?: string) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  return (
    <div className="space-y-6">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Promo Codes</h1>
          <p className="text-gray-600 mt-1">Manage discount codes and offers</p>
        </div>
        <button
          onClick={() => {
            setEditingPromo(null);
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Promo Code
        </button>
      </div>

      {/* Promo Codes List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : promoCodes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Tag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No promo codes yet</h3>
          <p className="text-gray-600">Create your first promo code to offer discounts</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {promoCodes.map((promo) => (
            <motion.div
              key={promo.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white rounded-lg shadow-md overflow-hidden border-2 ${
                !promo.isActive ? 'border-gray-300 opacity-60' :
                isExpired(promo.expiryDate) ? 'border-red-300' :
                'border-primary-200'
              }`}
            >
              {/* Code Header */}
              <div className={`p-4 ${
                !promo.isActive ? 'bg-gray-100' :
                isExpired(promo.expiryDate) ? 'bg-red-50' :
                'bg-gradient-to-r from-primary-600 to-secondary-600'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Tag className={`h-5 w-5 ${promo.isActive && !isExpired(promo.expiryDate) ? 'text-white' : 'text-gray-500'}`} />
                    <h3 className={`text-xl font-bold ${promo.isActive && !isExpired(promo.expiryDate) ? 'text-white' : 'text-gray-700'}`}>
                      {promo.code}
                    </h3>
                  </div>
                  <button
                    onClick={() => handleToggleActive(promo)}
                    className={`p-1.5 rounded-lg ${
                      promo.isActive ? 'bg-white/20 hover:bg-white/30' : 'bg-gray-200 hover:bg-gray-300'
                    }`}
                  >
                    {promo.isActive ? (
                      <Check className="h-4 w-4 text-white" />
                    ) : (
                      <X className="h-4 w-4 text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              {/* Details */}
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Discount</span>
                  <div className="flex items-center space-x-1">
                    {promo.discountType === 'percentage' ? (
                      <Percent className="h-4 w-4 text-green-600" />
                    ) : (
                      <DollarSign className="h-4 w-4 text-green-600" />
                    )}
                    <span className="text-lg font-bold text-green-600">
                      {promo.discountValue}
                      {promo.discountType === 'percentage' ? '%' : '₹'} OFF
                    </span>
                  </div>
                </div>

                {promo.description && (
                  <p className="text-sm text-gray-600">{promo.description}</p>
                )}

                {promo.minOrderValue && promo.minOrderValue > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Min Order</span>
                    <span className="font-medium">₹{promo.minOrderValue}</span>
                  </div>
                )}

                {promo.usageLimit && promo.usageLimit > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      Usage
                    </span>
                    <span className="font-medium">
                      {promo.usedCount} / {promo.usageLimit}
                    </span>
                  </div>
                )}

                {promo.expiryDate && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      Expires
                    </span>
                    <span className={`font-medium ${isExpired(promo.expiryDate) ? 'text-red-600' : ''}`}>
                      {new Date(promo.expiryDate).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {/* Status Badges */}
                <div className="flex flex-wrap gap-2 pt-2">
                  {!promo.isActive && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      Inactive
                    </span>
                  )}
                  {isExpired(promo.expiryDate) && (
                    <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full">
                      Expired
                    </span>
                  )}
                  {promo.usageLimit && promo.usedCount >= promo.usageLimit && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-600 text-xs rounded-full">
                      Limit Reached
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t border-gray-200">
                  <button
                    onClick={() => handleEdit(promo)}
                    className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(promo.id)}
                    className="flex-1 flex items-center justify-center px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowModal(false);
              setEditingPromo(null);
              resetForm();
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <form onSubmit={handleSubmit}>
                {/* Modal Header */}
                <div className="sticky top-0 bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-6 py-4 rounded-t-xl">
                  <h2 className="text-2xl font-bold">
                    {editingPromo ? 'Edit Promo Code' : 'Create New Promo Code'}
                  </h2>
                </div>

                {/* Modal Body */}
                <div className="p-6 space-y-4">
                  {/* Promo Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Promo Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="e.g., SUMMER20"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Brief description of the offer"
                      rows={2}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  {/* Discount Type and Value */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discount Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.discountType}
                        onChange={(e) => setFormData({ ...formData, discountType: e.target.value as 'percentage' | 'flat' })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="flat">Flat Amount (₹)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discount Value <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={formData.discountValue}
                        onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) || 0 })}
                        min="0"
                        max={formData.discountType === 'percentage' ? 100 : undefined}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                  </div>

                  {/* Min Order Value */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Order Value (₹)
                    </label>
                    <input
                      type="number"
                      value={formData.minOrderValue}
                      onChange={(e) => setFormData({ ...formData, minOrderValue: parseFloat(e.target.value) || 0 })}
                      min="0"
                      placeholder="0 = No minimum"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  {/* Usage Limit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Usage Limit
                    </label>
                    <input
                      type="number"
                      value={formData.usageLimit}
                      onChange={(e) => setFormData({ ...formData, usageLimit: parseInt(e.target.value) || 0 })}
                      min="0"
                      placeholder="0 = Unlimited"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  {/* Expiry Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expiry Date
                    </label>
                    <input
                      type="date"
                      value={formData.expiryDate}
                      onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  {/* Active Status */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                      Active (users can apply this code)
                    </label>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex gap-3 px-6 py-4 bg-gray-50 rounded-b-xl">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingPromo(null);
                      resetForm();
                    }}
                    className="flex-1 px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg hover:from-primary-700 hover:to-secondary-700 transition-all"
                  >
                    {editingPromo ? 'Update' : 'Create'} Promo Code
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
